import { useFavoriteTab } from '../../hooks/useFavoriteTab';
import React, { useState, useEffect, useRef } from 'react';
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  EyeSlashIcon,
  EyeIcon,
  UserIcon,
  UsersIcon,
  CogIcon,
  CommandLineIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  MinusIcon,
  XMarkIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import permissionsService, {
  AzureADGroup,
  AzureADUser,
  RouteDefinition,
  PagePermission,
  GroupMember,
  ApiEndpoint,
  ApiEndpointPermission,
  ApiEndpointPermissionInput,
  PermissionPage,
} from '../../services/permissions.service';
import UserImpersonationModal from '../../components/impersonation/UserImpersonationModal';
import { UnifiedPermissionsView } from '../../components/permissions/UnifiedPermissionsView';
import { NonPageRelatedView } from '../../components/permissions/NonPageRelatedView';
import ThreeStateToggle from '../../components/ThreeStateToggle';
import {
  GrantedFilter,
  GRANTED_FILTER_LABELS,
  grantedFilterToToggle,
  toggleToGrantedFilter,
} from '../../components/permissions/grantedFilter';

// Define module order to match navigation menu
const MODULE_ORDER = [
  'Fleet Management',
  'Orders',
  'Safety',
  'Analytics',
  'Data Management',
  'Development',
  'Administration'
];

const Permissions: React.FC = () => {
  const [groups, setGroups] = useState<AzureADGroup[]>([]);
  const [availableRoutes, setAvailableRoutes] = useState<RouteDefinition[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<AzureADGroup | null>(null);
  const [groupPermissions, setGroupPermissions] = useState<PagePermission[]>([]);
  const [selectedRoutes, setSelectedRoutes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [groupSearchFilter, setGroupSearchFilter] = useState<string>('');
  const [routeSearchFilter, setRouteSearchFilter] = useState<string>('');
  const [grantedFilterRoutes, setGrantedFilterRoutes] = useState<GrantedFilter>('all');
  const [hiddenGroupIds, setHiddenGroupIds] = useState<Set<number>>(new Set());
  const [showHidden, setShowHidden] = useState(false);
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);
  const [currentTab, setCurrentTab] = useState<'permissions' | 'non-page' | 'api-permissions' | 'users'>('permissions');
  useFavoriteTab(currentTab, setCurrentTab, ['permissions', 'non-page', 'api-permissions', 'users']);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberSearchFilter, setMemberSearchFilter] = useState<string>('');
  const [togglingGroupId, setTogglingGroupId] = useState<number | null>(null);

  // API Endpoint Permissions State
  const [apiEndpoints, setApiEndpoints] = useState<Record<string, ApiEndpoint[]>>({});
  const [apiEndpointPermissions, setApiEndpointPermissions] = useState<Map<number, { can_read: boolean; can_write: boolean }>>(new Map());
  const [permissionPages, setPermissionPages] = useState<PermissionPage[]>([]);
  const [loadingApiEndpoints, setLoadingApiEndpoints] = useState(false);
  const [savingApiPermissions, setSavingApiPermissions] = useState(false);
  const [apiEndpointSearchFilter, setApiEndpointSearchFilter] = useState<string>('');
  const [rebuildingEndpoints, setRebuildingEndpoints] = useState(false);
  const [editCategoriesMode, setEditCategoriesMode] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [pendingCategoryChanges, setPendingCategoryChanges] = useState<Map<number, string>>(new Map());
  const [savingCategories, setSavingCategories] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  // Bypassed Endpoints State
  const [bypassedEndpoints, setBypassedEndpoints] = useState<string[]>([]);
  const [loadingBypassedEndpoints, setLoadingBypassedEndpoints] = useState(false);

  // Add user to group state
  const [showAddUserPicker, setShowAddUserPicker] = useState(false);
  const [allUsers, setAllUsers] = useState<AzureADUser[]>([]);
  const [loadingAllUsers, setLoadingAllUsers] = useState(false);
  const [addingUserToGroup, setAddingUserToGroup] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [addUserSearchFilter, setAddUserSearchFilter] = useState('');

  // AbortController ref for initial load
  const initialLoadAbortRef = useRef<AbortController | null>(null);

  // Fetch bypassed endpoints
  const fetchBypassedEndpoints = async () => {
    try {
      setLoadingBypassedEndpoints(true);
      const response = await permissionsService.getBypassedEndpoints();
      setBypassedEndpoints(response.bypassed_endpoints || []);
    } catch (error) {
      console.error('Failed to fetch bypassed endpoints:', error);
    } finally {
      setLoadingBypassedEndpoints(false);
    }
  };

  // Fetch all groups from database
  const fetchGroups = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const data = await permissionsService.getAllGroups(signal);
      if (!signal?.aborted) setGroups(data);
    } catch (err: any) {
      if (signal?.aborted || err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
      setError(err.response?.data?.detail || 'Failed to fetch groups');
    } finally {
      // A canceled StrictMode/HMR request must not clear the replacement's spinner.
      if (!signal?.aborted) setLoading(false);
    }
  };

  // Fetch available routes
  const fetchRoutes = async (signal?: AbortSignal) => {
    try {
      const routes = await permissionsService.getAvailableRoutes(signal);
      setAvailableRoutes(routes);
    } catch (err: any) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
    }
  };

  // Toggle group enabled status
  const handleToggleGroup = async (group: AzureADGroup) => {
    try {
      setError(null);
      setTogglingGroupId(group.id);
      const newEnabledState = !group.enabled_for_permissions;
      await permissionsService.enableGroup(group.id, newEnabledState);
      setSuccess(
        `Group ${group.group_name} ${newEnabledState ? 'enabled' : 'disabled'} for permissions`
      );
      await fetchGroups();

      // If we disabled the currently selected group, clear selection
      if (selectedGroup?.id === group.id && group.enabled_for_permissions) {
        setSelectedGroup(null);
        setSelectedRoutes(new Set());
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update group');
    } finally {
      setTogglingGroupId(null);
    }
  };

  // Toggle group visibility (hide/show)
  const handleToggleHidden = (groupId: number) => {
    const newHidden = new Set(hiddenGroupIds);
    if (newHidden.has(groupId)) {
      newHidden.delete(groupId);
    } else {
      newHidden.add(groupId);
    }
    setHiddenGroupIds(newHidden);
  };

  // Select a group to manage permissions
  const handleSelectGroup = async (group: AzureADGroup) => {
    if (!group.enabled_for_permissions) return;

    try {
      setSelectedGroup(group);
      setLoading(true);
      setShowAddUserPicker(false);
      setAddUserSearchFilter('');

      // Load page permissions (always needed)
      const permissions = await permissionsService.getGroupPermissions(group.id);
      setGroupPermissions(permissions);

      // Set selected routes based on existing permissions
      const routePaths = new Set(permissions.map(p => p.route_path));
      setSelectedRoutes(routePaths);

      // Always load API endpoints and permissions for the unified view
      if (Object.keys(apiEndpoints).length === 0) {
        await fetchApiEndpoints();
      }
      await fetchGroupApiPermissions(group.id);

      // Refresh the current tab's data if needed
      if (currentTab === 'users') {
        await fetchGroupMembers(group.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch group permissions');
    } finally {
      setLoading(false);
    }
  };

  // Toggle route selection - also enables required API endpoints when enabling a page
  const handleToggleRoute = (routePath: string) => {
    const newSelection = new Set(selectedRoutes);
    const isEnabling = !newSelection.has(routePath);

    if (isEnabling) {
      newSelection.add(routePath);

      // Auto-enable required API endpoints for this page. Endpoints come
      // pre-resolved from the backend now (no regex matching needed).
      const page = permissionPages.find(p => p.route_path === routePath);
      if (page) {
        setApiEndpointPermissions(prev => {
          const newMap = new Map(prev);

          page.endpoints
            .filter(e => e.required)
            .forEach(ep => {
              const current = newMap.get(ep.endpoint_id) || { can_read: false, can_write: false };
              if (ep.method === 'GET') {
                newMap.set(ep.endpoint_id, { ...current, can_read: true });
              } else {
                newMap.set(ep.endpoint_id, { can_read: true, can_write: true });
              }
            });

          return newMap;
        });
      }
    } else {
      newSelection.delete(routePath);
      // Don't auto-disable endpoints - they might be shared with other pages
    }

    setSelectedRoutes(newSelection);
  };

  // Save permissions for selected group (page and API)
  const handleSavePermissions = async () => {
    if (!selectedGroup) return;

    try {
      setSaving(true);
      setError(null);

      // Build page permissions array
      const pagePermissions = Array.from(selectedRoutes).map(routePath => {
        const route = availableRoutes.find(r => r.path === routePath);
        return {
          route_path: routePath,
          route_name: route?.name || routePath,
          module_name: route?.module,
        };
      });

      // Build API permissions array from all endpoints
      const apiPermissions: ApiEndpointPermissionInput[] = [];
      Object.values(apiEndpoints).flat().forEach(endpoint => {
        const perm = apiEndpointPermissions.get(endpoint.id);
        apiPermissions.push({
          endpoint_id: endpoint.id,
          can_read: perm?.can_read || false,
          can_write: perm?.can_write || false,
        });
      });

      // Save page and API permissions
      await Promise.all([
        permissionsService.assignPagePermissions(selectedGroup.id, pagePermissions),
        permissionsService.setGroupApiPermissions(selectedGroup.id, apiPermissions),
      ]);

      setSuccess(`Successfully updated all permissions for ${selectedGroup.group_name}`);

      // Refresh permissions
      const updatedPermissions = await permissionsService.getGroupPermissions(selectedGroup.id);
      setGroupPermissions(updatedPermissions);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  // Filter and sort groups based on search (enabled groups first, then A–Z by name)
  const filteredGroups = groups
    .filter((group) => {
      // Only show GWI360-* groups, Developers, and Innovation Team
      const isAllowedGroup =
        group.group_name.startsWith('GWI360-') ||
        group.group_name === 'Developers' ||
        group.group_name === 'Innovation Team';

      if (!isAllowedGroup) return false;

      // Filter out hidden groups unless showHidden is true
      if (!showHidden && hiddenGroupIds.has(group.id)) return false;

      if (!groupSearchFilter) return true;
      const searchLower = groupSearchFilter.toLowerCase();
      return (
        group.group_name.toLowerCase().includes(searchLower) ||
        group.description?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      // Enabled groups first, then alphabetically by name (case-insensitive).
      if (a.enabled_for_permissions !== b.enabled_for_permissions) {
        return a.enabled_for_permissions ? -1 : 1;
      }
      return a.group_name.localeCompare(b.group_name, undefined, { sensitivity: 'base' });
    });

  // Filter routes based on search
  const filteredRoutes = availableRoutes.filter((route) => {
    if (!routeSearchFilter) return true;
    const searchLower = routeSearchFilter.toLowerCase();
    return (
      route.name.toLowerCase().includes(searchLower) ||
      route.path.toLowerCase().includes(searchLower) ||
      route.module.toLowerCase().includes(searchLower)
    );
  });

  // Group routes by module (using filtered routes)
  const routesByModule = filteredRoutes.reduce((acc, route) => {
    if (!acc[route.module]) {
      acc[route.module] = [];
    }
    acc[route.module].push(route);
    return acc;
  }, {} as Record<string, RouteDefinition[]>);

  useEffect(() => {
    const controller = new AbortController();
    initialLoadAbortRef.current = controller;

    fetchGroups(controller.signal);
    fetchRoutes(controller.signal);

    return () => initialLoadAbortRef.current?.abort();
  }, []);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Fetch group members from Azure AD
  const fetchGroupMembers = async (groupId: number) => {
    try {
      setLoadingMembers(true);
      const members = await permissionsService.getGroupMembers(groupId);
      setGroupMembers(members);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch group members');
      setGroupMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Open the add user picker and load all users
  const openAddUserPicker = async () => {
    setShowAddUserPicker(true);
    setAddUserSearchFilter('');
    if (allUsers.length > 0) return; // Already loaded

    try {
      setLoadingAllUsers(true);
      const users = await permissionsService.getAllUsers(false);
      setAllUsers(users);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoadingAllUsers(false);
    }
  };

  // Add a user to the currently selected group
  const handleAddUserToGroup = async (user: AzureADUser) => {
    if (!selectedGroup) return;

    try {
      setAddingUserToGroup(true);
      await permissionsService.addUserToGroup(user.user_id, selectedGroup.group_id);
      setShowAddUserPicker(false);
      setAddUserSearchFilter('');
      // Refresh member list
      await fetchGroupMembers(selectedGroup.id);
      setSuccess(`Added ${user.display_name} to ${selectedGroup.group_name}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add user to group');
    } finally {
      setAddingUserToGroup(false);
    }
  };

  // Remove a member from the currently selected group
  const handleRemoveMemberFromGroup = async (member: GroupMember) => {
    if (!selectedGroup) return;

    try {
      setRemovingMemberId(member.id);
      await permissionsService.removeUserFromGroup(member.id, selectedGroup.group_id);
      // Refresh member list
      await fetchGroupMembers(selectedGroup.id);
      setSuccess(`Removed ${member.displayName} from ${selectedGroup.group_name}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove user from group');
    } finally {
      setRemovingMemberId(null);
    }
  };

  // Filter users for the add user picker (exclude existing members)
  const filteredAddUsers = allUsers.filter((user) => {
    // Exclude users already in the group
    const isAlreadyMember = groupMembers.some(
      (m) => m.id === user.user_id || m.mail?.toLowerCase() === user.email?.toLowerCase()
    );
    if (isAlreadyMember) return false;

    if (!addUserSearchFilter) return true;
    const searchLower = addUserSearchFilter.toLowerCase();
    return (
      user.display_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.job_title?.toLowerCase().includes(searchLower) ||
      user.department?.toLowerCase().includes(searchLower)
    );
  });

  // Fetch API endpoints grouped by module + the page list (with assignments resolved server-side)
  const fetchApiEndpoints = async () => {
    try {
      setLoadingApiEndpoints(true);
      const [data, categories, pages] = await Promise.all([
        permissionsService.getApiEndpointsByModule(),
        permissionsService.getApiEndpointCategories(),
        permissionsService.getPermissionPages(),
      ]);
      setApiEndpoints(data.modules);
      setAvailableCategories(categories);
      setPermissionPages(pages);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch API endpoints');
      setApiEndpoints({});
      setPermissionPages([]);
    } finally {
      setLoadingApiEndpoints(false);
    }
  };

  // Assign an endpoint to a page; refresh the pages list so the new
  // assignment shows up under its target page on the next render.
  const handleAssignEndpointToPage = async (endpointId: number, pageRoute: string, required: boolean) => {
    try {
      await permissionsService.assignApiEndpointToPage(endpointId, pageRoute, required);
      const pages = await permissionsService.getPermissionPages();
      setPermissionPages(pages);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to assign endpoint to page');
    }
  };

  // Remove a page assignment; refresh.
  const handleRemovePageAssignment = async (assignmentId: number) => {
    try {
      await permissionsService.removeApiEndpointPageAssignment(assignmentId);
      const pages = await permissionsService.getPermissionPages();
      setPermissionPages(pages);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove page assignment');
    }
  };

  // Handle category change for an endpoint
  const handleCategoryChange = (endpointId: number, newCategory: string) => {
    const newChanges = new Map(pendingCategoryChanges);
    newChanges.set(endpointId, newCategory);
    setPendingCategoryChanges(newChanges);
  };

  // Save all pending category changes
  const handleSaveCategoryChanges = async () => {
    if (pendingCategoryChanges.size === 0) return;

    try {
      setSavingCategories(true);
      const updates = Array.from(pendingCategoryChanges.entries()).map(([endpoint_id, category]) => ({
        endpoint_id,
        category
      }));

      await permissionsService.bulkUpdateApiEndpointCategories(updates);
      setSuccess(`Updated categories for ${updates.length} endpoints`);
      setPendingCategoryChanges(new Map());
      setEditCategoriesMode(false);

      // Refresh endpoints
      await fetchApiEndpoints();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save category changes');
    } finally {
      setSavingCategories(false);
    }
  };

  // Fetch API endpoint permissions for selected group
  const fetchGroupApiPermissions = async (groupId: number) => {
    try {
      setLoadingApiEndpoints(true);
      const permissions = await permissionsService.getGroupApiPermissions(groupId);

      // Convert to map for easy lookup
      const permMap = new Map<number, { can_read: boolean; can_write: boolean }>();
      permissions.forEach(perm => {
        permMap.set(perm.endpoint_id, { can_read: perm.can_read, can_write: perm.can_write });
      });
      setApiEndpointPermissions(permMap);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch API permissions');
      setApiEndpointPermissions(new Map());
    } finally {
      setLoadingApiEndpoints(false);
    }
  };

  // Toggle API endpoint read permission
  const handleToggleApiRead = (endpointId: number) => {
    setApiEndpointPermissions(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(endpointId) || { can_read: false, can_write: false };
      newMap.set(endpointId, { ...current, can_read: !current.can_read });
      return newMap;
    });
  };

  // Toggle API endpoint write permission (write implies read)
  const handleToggleApiWrite = (endpointId: number) => {
    setApiEndpointPermissions(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(endpointId) || { can_read: false, can_write: false };
      const newWrite = !current.can_write;
      // If enabling write, also enable read
      newMap.set(endpointId, {
        can_read: newWrite ? true : current.can_read,
        can_write: newWrite
      });
      return newMap;
    });
  };

  // Toggle API endpoint access based on HTTP method
  // GET = read permission, PUT/POST/DELETE/PATCH = write permission
  const handleToggleApiAccess = (endpointId: number, method: string) => {
    setApiEndpointPermissions(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(endpointId) || { can_read: false, can_write: false };

      if (method === 'GET') {
        // Toggle read permission for GET endpoints
        newMap.set(endpointId, {
          can_read: !current.can_read,
          can_write: current.can_write
        });
      } else {
        // Toggle write permission for non-GET endpoints (write implies read)
        const newWrite = !current.can_write;
        newMap.set(endpointId, {
          can_read: newWrite ? true : current.can_read,
          can_write: newWrite
        });
      }
      return newMap;
    });
  };

  // Toggle category collapse
  const handleToggleCategory = (category: string) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Get permission counts for a category
  const getCategoryPermissionCounts = (endpoints: ApiEndpoint[]) => {
    let readCount = 0;
    let writeCount = 0;
    endpoints.forEach(ep => {
      const perm = apiEndpointPermissions.get(ep.id);
      if (perm?.can_read) readCount++;
      if (perm?.can_write) writeCount++;
    });
    return { readCount, writeCount, total: endpoints.length };
  };

  // Grant all read permissions for a category
  const handleGrantAllRead = (endpoints: ApiEndpoint[]) => {
    setApiEndpointPermissions(prev => {
      const newMap = new Map(prev);
      endpoints.forEach(ep => {
        const current = newMap.get(ep.id) || { can_read: false, can_write: false };
        newMap.set(ep.id, { ...current, can_read: true });
      });
      return newMap;
    });
  };

  // Grant all read+write permissions for a category
  const handleGrantAllReadWrite = (endpoints: ApiEndpoint[]) => {
    setApiEndpointPermissions(prev => {
      const newMap = new Map(prev);
      endpoints.forEach(ep => {
        newMap.set(ep.id, { can_read: true, can_write: true });
      });
      return newMap;
    });
  };

  // Revoke all permissions for a category
  const handleRevokeAll = (endpoints: ApiEndpoint[]) => {
    setApiEndpointPermissions(prev => {
      const newMap = new Map(prev);
      endpoints.forEach(ep => {
        newMap.set(ep.id, { can_read: false, can_write: false });
      });
      return newMap;
    });
  };

  // Grant all permissions (pages, API endpoints)
  const handleGrantAllPermissions = () => {
    // Select every known page route. availableRoutes and permissionPages
    // both come from permission_pages now; the union is just defensive.
    const allRoutes = new Set([
      ...availableRoutes.map(r => r.path),
      ...permissionPages.map(p => p.route_path),
    ]);
    setSelectedRoutes(allRoutes);

    // Enable read+write on all API endpoints
    setApiEndpointPermissions(prev => {
      const newMap = new Map(prev);
      Object.values(apiEndpoints).flat().forEach(ep => {
        newMap.set(ep.id, { can_read: true, can_write: true });
      });
      return newMap;
    });
  };

  // Remove all permissions (pages, API endpoints). Mirror of
  // handleGrantAllPermissions — stages everything as revoked locally; the
  // change isn't persisted until the user clicks "Save Changes".
  const handleRemoveAllPermissions = () => {
    // Deselect every page route.
    setSelectedRoutes(new Set());

    // Revoke read+write on all API endpoints.
    setApiEndpointPermissions(prev => {
      const newMap = new Map(prev);
      Object.values(apiEndpoints).flat().forEach(ep => {
        newMap.set(ep.id, { can_read: false, can_write: false });
      });
      return newMap;
    });
  };

  // Grant read on every API endpoint (covers GET / pull access), preserving
  // any existing write grant. Read/write is an API-endpoint concept only, so
  // this leaves page permissions untouched. Persisted on Save Changes.
  const handleGrantReadAll = () => {
    setApiEndpointPermissions(prev => {
      const newMap = new Map(prev);
      Object.values(apiEndpoints).flat().forEach(ep => {
        const current = newMap.get(ep.id) || { can_read: false, can_write: false };
        newMap.set(ep.id, { ...current, can_read: true });
      });
      return newMap;
    });
  };

  // Grant write on every API endpoint (covers POST/PUT/DELETE/PATCH access),
  // preserving any existing read grant. Persisted on Save Changes.
  const handleGrantWriteAll = () => {
    setApiEndpointPermissions(prev => {
      const newMap = new Map(prev);
      Object.values(apiEndpoints).flat().forEach(ep => {
        const current = newMap.get(ep.id) || { can_read: false, can_write: false };
        newMap.set(ep.id, { ...current, can_write: true });
      });
      return newMap;
    });
  };

  // Save API permissions for selected group
  const handleSaveApiPermissions = async () => {
    if (!selectedGroup) return;

    try {
      setSavingApiPermissions(true);
      setError(null);

      // Build permissions array from all endpoints
      const permissions: ApiEndpointPermissionInput[] = [];
      Object.values(apiEndpoints).flat().forEach(endpoint => {
        const perm = apiEndpointPermissions.get(endpoint.id);
        permissions.push({
          endpoint_id: endpoint.id,
          can_read: perm?.can_read || false,
          can_write: perm?.can_write || false,
        });
      });

      await permissionsService.setGroupApiPermissions(selectedGroup.id, permissions);
      setSuccess(`Successfully updated API permissions for ${selectedGroup.group_name}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save API permissions');
    } finally {
      setSavingApiPermissions(false);
    }
  };

  // Rebuild API endpoints from FastAPI routes
  const handleRebuildApiEndpoints = async () => {
    try {
      setRebuildingEndpoints(true);
      setError(null);
      const result = await permissionsService.rebuildApiEndpoints();
      setSuccess(`API endpoints rebuilt: ${result.stats.created} created, ${result.stats.updated} updated`);
      await fetchApiEndpoints();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to rebuild API endpoints');
    } finally {
      setRebuildingEndpoints(false);
    }
  };

  // Handle tab change (for the right panel tabs)
  const handleTabChange = async (tabId: 'permissions' | 'non-page' | 'api-permissions' | 'users') => {
    setCurrentTab(tabId);

    // Fetch group members when switching to Users tab
    if (tabId === 'users' && selectedGroup) {
      await fetchGroupMembers(selectedGroup.id);
    }
  };

  // Filter group members based on search, sorted alphabetically by name
  // (falling back to email), case-insensitive.
  const filteredMembers = groupMembers
    .filter((member) => {
      if (!memberSearchFilter) return true;
      const searchLower = memberSearchFilter.toLowerCase();
      return (
        member.displayName?.toLowerCase().includes(searchLower) ||
        member.mail?.toLowerCase().includes(searchLower) ||
        member.jobTitle?.toLowerCase().includes(searchLower) ||
        member.department?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) =>
      (a.displayName || a.mail || '').localeCompare(b.displayName || b.mail || '', undefined, { sensitivity: 'base' })
    );

  return (
    <div className="p-6 space-y-6 mb-16">

      {/* Error Alert */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-400">Error</h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Success Alert */}
      {success && (
        <div className="rounded-md bg-green-50 dark:bg-green-900/20 p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700 dark:text-green-300">{success}</p>
            </div>
            <button
              onClick={() => setSuccess(null)}
              className="ml-auto text-green-500 hover:text-green-700"
            >
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[calc(100vh-12rem)]">
        {/* Left Column - Groups List */}
        <div className="bg-panel shadow rounded-lg h-full flex flex-col">
          <div className="px-4 py-5 sm:p-6 flex-1 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-heading">
                Azure AD Groups
              </h3>
              <div className="flex items-center gap-2">
                {hiddenGroupIds.size > 0 && (
                  <button
                    onClick={() => setShowHidden(!showHidden)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs font-medium text-label bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    {showHidden ? (
                      <>
                        <EyeSlashIcon className="h-4 w-4 mr-1.5" />
                        Hide Hidden ({hiddenGroupIds.size})
                      </>
                    ) : (
                      <>
                        <EyeIcon className="h-4 w-4 mr-1.5" />
                        Show Hidden ({hiddenGroupIds.size})
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={handleRebuildApiEndpoints}
                  disabled={rebuildingEndpoints}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  <ArrowPathIcon className={`-ml-1 mr-1.5 h-4 w-4 ${rebuildingEndpoints ? 'animate-spin' : ''}`} />
                  {rebuildingEndpoints ? 'Rebuilding...' : 'Rebuild Endpoints'}
                </button>
              </div>
            </div>

            {/* Search Filter for Groups */}
            {groups.length > 0 && (
              <div className="mb-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={groupSearchFilter}
                    onChange={(e) => setGroupSearchFilter(e.target.value)}
                    placeholder="Search groups..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            )}

            {loading && groups.length === 0 ? (
              <div className="text-center py-8">
                <ArrowPathIcon className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
                <p className="mt-2 text-sm text-muted">Loading groups...</p>
              </div>
            ) : groups.length === 0 ? (
              <div className="text-center py-8">
                <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-muted">
                  No groups found. Groups sync automatically from Azure AD via the Sync worker.
                </p>
              </div>
            ) : filteredGroups.length === 0 ? (
              <div className="text-center py-8">
                <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-muted">
                  No groups match your search.
                </p>
              </div>
            ) : (
              <div className="space-y-2 flex-1 overflow-y-auto">
                {filteredGroups.map((group) => {
                  return (
                    <div
                      key={group.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        selectedGroup?.id === group.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      } ${group.enabled_for_permissions ? 'cursor-pointer' : 'opacity-60'}`}
                      onClick={() => group.enabled_for_permissions && handleSelectGroup(group)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-heading truncate">
                          {group.group_name}
                        </p>
                        {group.description && (
                          <p className="text-xs text-muted truncate">
                            {group.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleGroup(group);
                          }}
                          disabled={togglingGroupId === group.id}
                          className={`px-3 py-1 rounded text-xs font-medium inline-flex items-center gap-1.5 ${
                            group.enabled_for_permissions
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                          } ${togglingGroupId === group.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {togglingGroupId === group.id && (
                            <ArrowPathIcon className="h-3 w-3 animate-spin" />
                          )}
                          {group.enabled_for_permissions ? 'Enabled' : 'Disabled'}
                        </button>
                        {!group.enabled_for_permissions && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleHidden(group.id);
                            }}
                            className="p-1.5 rounded text-muted hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            title={hiddenGroupIds.has(group.id) ? 'Unhide' : 'Hide'}
                          >
                            {hiddenGroupIds.has(group.id) ? (
                              <EyeIcon className="h-4 w-4" />
                            ) : (
                              <EyeSlashIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Group Configuration with Tabs */}
        <div className="bg-panel shadow rounded-lg h-full flex flex-col">
          <div className="relative px-4 py-5 sm:p-6 flex-1 flex flex-col">
            {!selectedGroup ? (
              <div className="text-center py-16">
                <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-muted">
                  Select an enabled group to manage its configuration
                </p>
              </div>
            ) : (
              <>
                {/* Group Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-heading">
                    {selectedGroup.group_name}
                  </h3>
                  <div className="flex flex-wrap justify-end gap-2">
                    <button
                      onClick={handleGrantAllPermissions}
                      className="inline-flex items-center px-3 py-1.5 border border-green-300 dark:border-green-600 rounded-md shadow-sm text-xs font-medium text-green-700 dark:text-green-300 bg-white dark:bg-gray-700 hover:bg-green-50 dark:hover:bg-green-900"
                    >
                      <CheckCircleIcon className="-ml-1 mr-1.5 h-4 w-4" />
                      Grant All Permissions
                    </button>
                    <button
                      onClick={handleGrantReadAll}
                      className="inline-flex items-center px-3 py-1.5 border border-blue-300 dark:border-blue-600 rounded-md shadow-sm text-xs font-medium text-blue-700 dark:text-blue-300 bg-white dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900"
                      title="Grant read (GET / pull) access on all API endpoints"
                    >
                      <EyeIcon className="-ml-1 mr-1.5 h-4 w-4" />
                      Grant Read (All)
                    </button>
                    <button
                      onClick={handleGrantWriteAll}
                      className="inline-flex items-center px-3 py-1.5 border border-amber-300 dark:border-amber-600 rounded-md shadow-sm text-xs font-medium text-amber-700 dark:text-amber-300 bg-white dark:bg-gray-700 hover:bg-amber-50 dark:hover:bg-amber-900"
                      title="Grant write (POST/PUT/DELETE/PATCH) access on all API endpoints"
                    >
                      <PencilSquareIcon className="-ml-1 mr-1.5 h-4 w-4" />
                      Grant Write (All)
                    </button>
                    <button
                      onClick={handleRemoveAllPermissions}
                      className="inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-600 rounded-md shadow-sm text-xs font-medium text-red-700 dark:text-red-300 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900"
                    >
                      <XCircleIcon className="-ml-1 mr-1.5 h-4 w-4" />
                      Remove All Permissions
                    </button>
                    <button
                      onClick={() => setShowImpersonationModal(true)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-xs font-medium text-label bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      <UserIcon className="-ml-1 mr-1.5 h-4 w-4" />
                      Impersonate User
                    </button>
                    {(currentTab === 'permissions' || currentTab === 'non-page') && (
                      <button
                        onClick={handleSavePermissions}
                        disabled={saving}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Tabs inside the card */}
                <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                  <nav className="-mb-px flex space-x-6">
                    <button
                      onClick={() => handleTabChange('permissions')}
                      className={`flex items-center py-2 px-1 border-b-2 text-sm font-medium ${
                        currentTab === 'permissions'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-muted hover:text-gray-700 hover:border-gray-300 dark:hover:text-gray-300'
                      }`}
                    >
                      <ShieldCheckIcon className="h-4 w-4 mr-1.5" />
                      Permissions
                    </button>
                    <button
                      onClick={() => handleTabChange('non-page')}
                      className={`flex items-center py-2 px-1 border-b-2 text-sm font-medium ${
                        currentTab === 'non-page'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-muted hover:text-gray-700 hover:border-gray-300 dark:hover:text-gray-300'
                      }`}
                    >
                      <ShieldCheckIcon className="h-4 w-4 mr-1.5" />
                      Non Page Related
                    </button>
                    <button
                      onClick={() => handleTabChange('users')}
                      className={`flex items-center py-2 px-1 border-b-2 text-sm font-medium ${
                        currentTab === 'users'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-muted hover:text-gray-700 hover:border-gray-300 dark:hover:text-gray-300'
                      }`}
                    >
                      <UsersIcon className="h-4 w-4 mr-1.5" />
                      Users
                    </button>
                  </nav>
                </div>

                {/* Loading Overlay */}
                {loading && (
                  <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
                    <div className="text-center">
                      <ArrowPathIcon className="mx-auto h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading permissions...</p>
                    </div>
                  </div>
                )}

                {/* Unified Permissions Tab Content */}
                {currentTab === 'permissions' && (
                  <>
                    {/* Search Filter */}
                    <div className="mb-4 flex items-center gap-3">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={routeSearchFilter}
                          onChange={(e) => setRouteSearchFilter(e.target.value)}
                          placeholder="Search pages and endpoints (space-separated terms all must match)..."
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div
                        className="flex items-center gap-2 whitespace-nowrap"
                        title="All: show everything · Hide Granted: only what's missing · Only Granted: only what this group has"
                      >
                        <span className="text-xs text-muted">Show:</span>
                        <ThreeStateToggle
                          size="sm"
                          ariaLabel="Filter endpoints by grant state"
                          value={grantedFilterToToggle(grantedFilterRoutes)}
                          onChange={(v) => setGrantedFilterRoutes(toggleToGrantedFilter(v))}
                          labels={GRANTED_FILTER_LABELS}
                        />
                      </div>
                    </div>

                    {/* Unified View Component */}
                    <div className="flex-1 overflow-y-auto">
                      <UnifiedPermissionsView
                        selectedRoutes={selectedRoutes}
                        onToggleRoute={handleToggleRoute}
                        apiEndpoints={apiEndpoints}
                        apiEndpointPermissions={apiEndpointPermissions}
                        permissionPages={permissionPages}
                        onRemovePageAssignment={handleRemovePageAssignment}
                        onToggleApiRead={handleToggleApiRead}
                        onToggleApiWrite={handleToggleApiWrite}
                        onToggleApiAccess={handleToggleApiAccess}
                        searchFilter={routeSearchFilter}
                        grantedFilter={grantedFilterRoutes}
                        moduleOrder={MODULE_ORDER}
                      />
                    </div>
                  </>
                )}

                {/* Non Page Related Tab Content */}
                {currentTab === 'non-page' && (
                  <div className="flex-1 overflow-y-auto">
                    <NonPageRelatedView
                      apiEndpoints={apiEndpoints}
                      apiEndpointPermissions={apiEndpointPermissions}
                      permissionPages={permissionPages}
                      onAssignEndpointToPage={handleAssignEndpointToPage}
                      onToggleApiAccess={handleToggleApiAccess}
                    />
                  </div>
                )}

                {/* Users Tab Content */}
                {currentTab === 'users' && (
                  <>
                    {/* Search and Add User Row */}
                    <div className="mb-4 flex gap-2">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={memberSearchFilter}
                          onChange={(e) => setMemberSearchFilter(e.target.value)}
                          placeholder="Search members..."
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <button
                        onClick={openAddUserPicker}
                        className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        title="Add user to group"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add User
                      </button>
                    </div>

                    {/* Add User Picker */}
                    {showAddUserPicker && (
                      <div className="mb-4 border border-blue-200 dark:border-blue-700 rounded-md bg-blue-50 dark:bg-blue-900/20 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-blue-800 dark:text-blue-300">Add user to {selectedGroup?.group_name}</span>
                          <button
                            onClick={() => { setShowAddUserPicker(false); setAddUserSearchFilter(''); }}
                            className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={addUserSearchFilter}
                            onChange={(e) => setAddUserSearchFilter(e.target.value)}
                            placeholder="Search users by name or email..."
                            autoFocus
                            className="block w-full pl-9 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        {loadingAllUsers ? (
                          <div className="flex items-center gap-2 py-3 justify-center">
                            <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-400" />
                            <span className="text-sm text-gray-500">Loading users...</span>
                          </div>
                        ) : (
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {filteredAddUsers.length === 0 ? (
                              <p className="text-xs text-muted py-2 text-center">
                                {addUserSearchFilter ? 'No matching users found' : 'All users are already in this group'}
                              </p>
                            ) : (
                              filteredAddUsers.slice(0, 50).map((user) => (
                                <button
                                  key={user.id}
                                  onClick={() => handleAddUserToGroup(user)}
                                  disabled={addingUserToGroup}
                                  className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50"
                                >
                                  <div className="h-7 w-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                    <UserIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-heading truncate">
                                      {user.display_name}
                                    </p>
                                    <p className="text-xs text-muted truncate">
                                      {user.email}
                                      {user.job_title && ` • ${user.job_title}`}
                                    </p>
                                  </div>
                                </button>
                              ))
                            )}
                            {filteredAddUsers.length > 50 && (
                              <p className="text-xs text-gray-400 text-center py-1">
                                Showing 50 of {filteredAddUsers.length} — type to narrow results
                              </p>
                            )}
                          </div>
                        )}
                        {addingUserToGroup && (
                          <div className="flex items-center gap-2 py-1">
                            <ArrowPathIcon className="h-4 w-4 animate-spin text-blue-500" />
                            <span className="text-sm text-blue-600 dark:text-blue-400">Adding user to group...</span>
                          </div>
                        )}
                      </div>
                    )}

                    {loadingMembers ? (
                      <div className="text-center py-8">
                        <ArrowPathIcon className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
                        <p className="mt-2 text-sm text-muted">Loading members from Azure AD...</p>
                      </div>
                    ) : groupMembers.length === 0 ? (
                      <div className="text-center py-8">
                        <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-heading">No Members Found</h3>
                        <p className="mt-1 text-sm text-muted">
                          This group has no members, or the app lacks permission to read group members.
                        </p>
                        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                          Requires AZURE_AD_CLIENT_SECRET to be configured in the backend.
                        </p>
                      </div>
                    ) : filteredMembers.length === 0 ? (
                      <div className="text-center py-8">
                        <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-muted">
                          No members match your search.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2 flex-1 overflow-y-auto">
                        <p className="text-xs text-muted mb-2">
                          {filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''} in this group
                        </p>
                        {filteredMembers.map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                          >
                            <div className="flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <UserIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              </div>
                            </div>
                            <div className="ml-3 flex-1 min-w-0">
                              <p className="text-sm font-medium text-heading truncate">
                                {member.displayName}
                              </p>
                              <p className="text-xs text-muted truncate">
                                {member.mail || member.userPrincipalName}
                              </p>
                              {(member.jobTitle || member.department) && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                  {[member.jobTitle, member.department].filter(Boolean).join(' • ')}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveMemberFromGroup(member)}
                              disabled={removingMemberId === member.id}
                              className="ml-2 p-1.5 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50"
                              title={`Remove ${member.displayName} from group`}
                            >
                              {removingMemberId === member.id ? (
                                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                              ) : (
                                <MinusIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

              </>
            )}
          </div>
        </div>
      </div>

      {/* Impersonation Modal */}
      <UserImpersonationModal
        isOpen={showImpersonationModal}
        onClose={() => setShowImpersonationModal(false)}
      />
    </div>
  );
};

export default Permissions;
